import { useState, useEffect, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { dashboardService } from '../../../services/dashboard.service';
import { alumniService } from '../../../services/alumni.service';
import { formatIndianCurrency, formatIndianNumber } from '../../../utils/currencyFormatter';
import {
  MagnifyingGlass,
  Spinner,
  User,
  Building,
  IdentificationCard,
  ChartBar,
  CalendarBlank,
  TrendUp,
  TrendDown,
  X,
  ArrowsLeftRight,
  Receipt,
} from '@phosphor-icons/react';

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

const formatInr = (amount: number | string | undefined) => {
  if (!amount || amount === '0') return 'N/A';
  const num = typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.]/g, '')) : amount;
  if (isNaN(num) || num === 0) return amount;

  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
  return `₹${num.toLocaleString('en-IN')}`;
};

// Same parser used in AlumniDetailView.tsx for FY giving amounts
const parseMoney = (val: unknown): number => {
  if (typeof val === 'number') return val;
  if (typeof val !== 'string') return 0;
  const cleaned = val.replace(/[^0-9.-]/g, '');
  return Number(cleaned) || 0;
};

const getDonorMetrics = (donor: any) => {
  if (!donor) return null;
  const vals = donor.vals_framework || {};
  return {
    netWorth: Number(vals.estimated_financial_capacity?.parsed_net_worth?.low) || 0,
    targetAsk: Number(vals.estimated_financial_capacity?.parsed_recommended_ask?.low) || 0,
    cumGiving: parseMoney(donor.source_info?.cumulative_giving_to_iitm),
    givingPropensity: Number(vals.donation_propensity) || 0,
  };
};

type MetricKey = 'netWorth' | 'targetAsk' | 'cumGiving' | 'givingPropensity';
type ComparisonMap = Partial<Record<MetricKey, 'higher' | 'lower' | 'equal'>>;

const buildComparison = (mine: ReturnType<typeof getDonorMetrics>, other: ReturnType<typeof getDonorMetrics>): ComparisonMap | null => {
  if (!mine || !other) return null;
  const keys: MetricKey[] = ['netWorth', 'targetAsk', 'cumGiving', 'givingPropensity'];
  const result: ComparisonMap = {};
  keys.forEach((k) => {
    if (mine[k] === other[k]) result[k] = 'equal';
    else result[k] = mine[k] > other[k] ? 'higher' : 'lower';
  });
  return result;
};

const buildFyDonations = (donor: any) => {
  const sInfo = donor?.source_info || {};
  return [
    { year: 'FY21', amount: parseMoney(sInfo.amount_donated_in_the_fy21) },
    { year: 'FY22', amount: parseMoney(sInfo.amount_donated_in_the_fy22) },
    { year: 'FY23', amount: parseMoney(sInfo.amount_donated_in_the_fy23) },
    { year: 'FY24', amount: parseMoney(sInfo.amount_donated_in_the_fy24) },
    { year: 'FY25', amount: parseMoney(sInfo.amount_donated_in_the_fy25) },
    { year: 'FY26', amount: parseMoney(sInfo.amount_donated_in_the_fy26) },
  ];
};

// ─────────────────────────────────────────────────────────────────────────
// Enrichment: search results are lightweight (identity + source_info only),
// so once a donor is selected we fetch the full profile to populate
// monday_data and FY giving fields, the same data AlumniDetailView uses.
// ─────────────────────────────────────────────────────────────────────────

const useEnrichedDonor = (donor: any) => {
  const [enriched, setEnriched] = useState<any>(null);
  const [enriching, setEnriching] = useState(false);
  const [enrichError, setEnrichError] = useState(false);

  useEffect(() => {
    if (!donor) {
      setEnriched(null);
      setEnrichError(false);
      return;
    }

    // Already has the fields we need (e.g. re-selected from an already-enriched object)
    if (donor.monday_data || donor.source_info?.amount_donated_in_the_fy24 !== undefined) {
      setEnriched(donor);
      setEnrichError(false);
      return;
    }

    let cancelled = false;
    setEnriched(donor); // render basic info immediately, don't block on the network
    setEnriching(true);
    setEnrichError(false);

    const donorId = donor.donor_id || donor._id;

    alumniService
      .getAlumniById(donorId)
      .then((full: any) => {
        if (cancelled) return;
        setEnriched((prev: any) => ({ ...prev, ...full }));
      })
      .catch((err: any) => {
        console.error('Failed to load full donor profile for comparison:', err);
        if (!cancelled) setEnrichError(true);
      })
      .finally(() => {
        if (!cancelled) setEnriching(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donor?.donor_id, donor?._id]);

  return { donor: enriched, enriching, enrichError };
};

// ─────────────────────────────────────────────────────────────────────────
// Donor Selector
// ─────────────────────────────────────────────────────────────────────────

const DonorSelector = ({ onSelect, placeholder }: { onSelect: (d: any) => void; placeholder: string }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searched, setSearched] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await dashboardService.searchAll(query);
        setResults(res.alumni || []);
        setShowDropdown(true);
        setSearched(true);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div className="relative flex items-center">
        <MagnifyingGlass className="absolute left-4 text-slate-400" size={20} />
        <input
          type="text"
          placeholder={placeholder}
          className="w-full bg-slate-50 border border-slate-200 rounded-2xl py-3 pl-12 pr-4 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-shadow"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            if (results.length > 0) setShowDropdown(true);
          }}
        />
        {loading && <Spinner className="absolute right-4 animate-spin text-accent" size={20} />}
      </div>

      {showDropdown && (
        <div className="absolute top-full mt-2 left-0 w-full bg-white border border-slate-200 shadow-xl rounded-2xl max-h-[300px] overflow-y-auto z-50">
          {results.length > 0 ? (
            results.map((alumni) => (
              <div
                key={alumni._id}
                className="p-3 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 cursor-pointer flex flex-col gap-1 transition-colors"
                onClick={() => {
                  onSelect(alumni);
                  setShowDropdown(false);
                  setQuery('');
                }}
              >
                <div className="font-bold text-slate-800 text-sm">
                  {alumni.source_info?.current_name || alumni.linkedin_info?.name || alumni.donor_id}
                </div>
                <div className="text-[11px] text-slate-500 font-medium flex items-center gap-1.5">
                  <span className="truncate max-w-[120px]">{alumni.source_info?.company || 'No Company'}</span>
                  <span>•</span>
                  <span className="font-mono bg-slate-100 px-1 rounded">{alumni.donor_id}</span>
                </div>
              </div>
            ))
          ) : searched && !loading ? (
            <div className="p-4 text-center text-xs font-medium text-slate-400">No matching donors found.</div>
          ) : null}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Delta badge — shown on metric tiles when both donors are loaded
// ─────────────────────────────────────────────────────────────────────────

const DeltaBadge = ({ status }: { status?: 'higher' | 'lower' | 'equal' }) => {
  if (!status || status === 'equal') return null;
  const isHigher = status === 'higher';
  const Icon = isHigher ? TrendUp : TrendDown;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-[3px] rounded-full ${
        isHigher ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'
      }`}
    >
      <Icon size={10} weight="bold" />
      {isHigher ? 'HIGHER' : 'LOWER'}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Donor Card
// ─────────────────────────────────────────────────────────────────────────

type ViewTab = 'profile' | 'giving' | 'transactions';

const DonorCard = ({
  donor,
  enriching,
  view,
  comparison,
  onClear,
  accentColor,
}: {
  donor: any;
  enriching?: boolean;
  view: ViewTab;
  comparison: ComparisonMap | null;
  onClear: () => void;
  accentColor: string;
}) => {
  if (!donor) {
    return (
      <div className="h-full min-h-[540px] border-2 border-dashed border-slate-200 rounded-3xl flex flex-col items-center justify-center text-slate-400 p-8 text-center bg-slate-50/50">
        <User size={48} weight="fill" className="mb-4 text-slate-300" />
        <p className="font-bold text-slate-500 mb-1">No Donor Selected</p>
        <p className="text-xs font-medium">Search and select a donor to view their comparison profile.</p>
      </div>
    );
  }

  const name = donor.source_info?.current_name || donor.linkedin_info?.name || donor.donor_id;
  const company = donor.source_info?.company || donor.linkedin_info?.headline || 'Unknown Company';
  const rollNumber = donor.source_info?.roll_number || donor.donor_id || 'N/A';

  const vals = donor.vals_framework || {};
  const scores = vals.scores || {};
  const getScore = (key: string) => {
    const s = scores[key];
    return typeof s === 'object' ? s.score || 0 : s || 0;
  };

  const barData = [
    { name: 'Research Advancement', value: getScore('research_advancement') },
    { name: 'Founder Innovation', value: getScore('founder_innovation') },
    { name: 'Social Impact', value: getScore('social_national_impact') },
    { name: 'Public Influence', value: getScore('public_influence') },
    { name: 'Student Development', value: getScore('student_development') },
    { name: 'Legacy', value: getScore('legacy_recognition') },
    { name: 'IITM Affinity', value: getScore('iitm_affinity') },
    { name: 'Philanthropic History', value: getScore('philanthropic_history') },
  ];

  const barOptions = {
    grid: { top: 0, bottom: 0, left: '2%', right: '10%', containLabel: true },
    xAxis: {
      type: 'value',
      max: 10,
      splitLine: { show: false },
      axisLabel: { show: false },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'category',
      data: barData.map((d) => d.name),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { fontSize: 10, fontWeight: 600, color: '#475569', fontFamily: 'Outfit, sans-serif' },
    },
    series: [
      {
        type: 'bar',
        data: barData.map((d) => ({ value: d.value, itemStyle: { color: accentColor, borderRadius: [0, 4, 4, 0] } })),
        barWidth: 10,
        label: { show: true, position: 'right', fontSize: 10, color: '#64748b', formatter: '{c}' },
      },
    ],
  };

  const netWorth = vals.estimated_financial_capacity?.parsed_net_worth?.low || 0;
  const targetAsk = vals.estimated_financial_capacity?.parsed_recommended_ask?.low || 0;
  const cumGiving = donor.source_info?.cumulative_giving_to_iitm || '0';
  const givingPropensity = vals.donation_propensity || 0;

  const fyDonations = buildFyDonations(donor);
  const hasFYDonations = fyDonations.some((d) => d.amount > 0);
  const mondayData = (donor.monday_data ?? []) as any[];

  return (
    <div className="h-full min-h-[540px] border border-slate-200/60 rounded-3xl p-6 bg-white shadow-sm flex flex-col gap-5 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-2.5 min-w-0">
          <h3 className="font-outfit font-extrabold text-2xl text-slate-900 leading-tight truncate">{name}</h3>
          <div className="flex flex-col gap-1.5 text-[13px] font-medium text-slate-500 font-sans">
            <div className="flex items-center gap-2">
              <Building size={16} className="text-slate-400 shrink-0" /> <span className="truncate">{company}</span>
            </div>
            <div className="flex items-center gap-2">
              <IdentificationCard size={16} className="text-slate-400 shrink-0" /> {rollNumber}
            </div>
          </div>
        </div>
        <button
          onClick={onClear}
          className="shrink-0 w-8 h-8 rounded-full bg-slate-50 hover:bg-rose-50 hover:text-rose-500 text-slate-400 flex items-center justify-center transition-colors"
          title="Clear selection"
        >
          <X size={16} weight="bold" />
        </button>
      </div>

      {/* Body: switches with the shared view tab */}
      <div className="flex-1 min-h-0 flex flex-col">
        {view === 'profile' && (
          <div className="flex-1 flex flex-col gap-5">
            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100/50 flex-1 flex flex-col justify-center min-h-[200px]">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 text-center">
                Psychographic Profile
              </p>
              <ReactECharts option={barOptions} style={{ height: '180px', width: '100%' }} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-900 rounded-2xl p-3.5 flex flex-col gap-1 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-16 h-16 bg-white/5 rounded-full blur-xl translate-x-1/2 -translate-y-1/2" />
                <div className="flex items-center justify-between relative z-10">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Target Ask</span>
                  <DeltaBadge status={comparison?.targetAsk} />
                </div>
                <span className="text-lg font-outfit font-black text-white relative z-10">{formatInr(targetAsk)}</span>
              </div>
              <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-3.5 flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <span className="text-[9px] font-bold text-emerald-600/70 uppercase tracking-wider">Propensity</span>
                  <DeltaBadge status={comparison?.givingPropensity} />
                </div>
                <span className="text-lg font-outfit font-black text-emerald-700">{givingPropensity}%</span>
              </div>
              <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3.5 flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Net Worth</span>
                  <DeltaBadge status={comparison?.netWorth} />
                </div>
                <span className="text-sm font-outfit font-black text-slate-800">{formatInr(netWorth)}</span>
              </div>
              <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3.5 flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Cum. Giving</span>
                  <DeltaBadge status={comparison?.cumGiving} />
                </div>
                <span className="text-sm font-outfit font-black text-slate-800">{formatInr(cumGiving)}</span>
              </div>
            </div>
          </div>
        )}

        {view === 'giving' && (
          <div className="flex-1 flex flex-col">
            <div className="flex items-center gap-2 mb-4">
              <ChartBar size={18} className="text-slate-700" weight="duotone" />
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Financial Year Giving</p>
              {enriching && <Spinner size={13} className="animate-spin text-accent ml-1" />}
            </div>

            {hasFYDonations ? (
              <div className="h-64 w-full flex-1">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={fyDonations} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id={`fyGivingFill-${donor.donor_id || donor._id}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f97316" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="year" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                    <YAxis
                      tickFormatter={(v) => formatIndianCurrency(Number(v), '₹0')}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 12, fill: '#64748b' }}
                    />
                    <RechartsTooltip
                      formatter={(value: any) => [formatIndianCurrency(Number(value), '₹0'), 'Amount']}
                      cursor={{ stroke: '#fdba74', strokeWidth: 1, strokeDasharray: '4 4' }}
                      contentStyle={{
                        borderRadius: 12,
                        border: '1px solid #fed7aa',
                        boxShadow: '0 8px 24px -8px rgba(15,23,42,0.15)',
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="amount"
                      stroke="#ea580c"
                      strokeWidth={3}
                      fill={`url(#fyGivingFill-${donor.donor_id || donor._id})`}
                      dot={{ r: 4, fill: '#ea580c', strokeWidth: 2, stroke: '#fff' }}
                      activeDot={{ r: 6, fill: '#ea580c', strokeWidth: 3, stroke: '#fff' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex-1 min-h-[220px] flex flex-col items-center justify-center text-center bg-slate-50/60 border border-dashed border-slate-200 rounded-2xl p-6">
                <ChartBar size={32} className="text-slate-300 mb-3" weight="duotone" />
                <p className="text-sm font-bold text-slate-500">No FY giving data available</p>
                <p className="text-xs font-medium text-slate-400 mt-1">
                  {enriching ? 'Still loading donation history…' : 'This donor has no recorded financial year contributions.'}
                </p>
              </div>
            )}
          </div>
        )}

        {view === 'transactions' && (
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex items-center gap-2 mb-3 shrink-0">
              <CalendarBlank size={18} className="text-slate-700" weight="duotone" />
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Monday Transactions</p>
              {enriching && <Spinner size={13} className="animate-spin text-accent ml-1" />}
            </div>

            {mondayData.length > 0 ? (
              <div className="flex-1 min-h-[220px] max-h-[340px] overflow-y-auto rounded-2xl border border-slate-100">
                <table className="w-full text-left border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-slate-50/95 backdrop-blur border-b border-slate-200/70">
                      <th className="py-2.5 px-4 text-[9px] font-bold text-slate-400 uppercase tracking-wider">Date</th>
                      <th className="py-2.5 px-3 text-[9px] font-bold text-slate-400 uppercase tracking-wider">Amount</th>
                      <th className="py-2.5 px-3 text-[9px] font-bold text-slate-400 uppercase tracking-wider">Project</th>
                      <th className="py-2.5 px-3 text-[9px] font-bold text-slate-400 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mondayData.map((tx, idx) => (
                      <tr key={idx} className="border-b border-slate-100 last:border-0 hover:bg-orange-50/40 transition-colors">
                        <td className="py-2.5 px-4 text-xs text-slate-600 whitespace-nowrap">
                          {tx.date?.$date ? new Date(tx.date.$date).toLocaleDateString() : typeof tx.date === 'string' ? tx.date : 'N/A'}
                        </td>
                        <td className="py-2.5 px-3 text-xs font-semibold text-slate-800 whitespace-nowrap">
                          {tx.currency} {formatIndianNumber(tx.amount, '0')}
                        </td>
                        <td className="py-2.5 px-3 text-xs text-slate-700 truncate max-w-[120px]">{tx.project || 'N/A'}</td>
                        <td className="py-2.5 px-3">
                          {tx.paid ? (
                            <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full text-[9px] font-bold">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> PAID
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full text-[9px] font-bold">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> PENDING
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex-1 min-h-[220px] flex flex-col items-center justify-center text-center bg-slate-50/60 border border-dashed border-slate-200 rounded-2xl p-6">
                <Receipt size={32} className="text-slate-300 mb-3" weight="duotone" />
                <p className="text-sm font-bold text-slate-500">No transactions found</p>
                <p className="text-xs font-medium text-slate-400 mt-1">
                  {enriching ? 'Still loading transaction history…' : 'This donor has no recorded Monday.com transactions.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────

const TABS: { key: ViewTab; label: string; icon: any }[] = [
  { key: 'profile', label: 'Profile & VALS', icon: User },
  { key: 'giving', label: 'Giving History', icon: ChartBar },
  { key: 'transactions', label: 'Transactions', icon: Receipt },
];

export const DonorComparison = () => {
  const [donor1Raw, setDonor1Raw] = useState<any>(null);
  const [donor2Raw, setDonor2Raw] = useState<any>(null);
  const [view, setView] = useState<ViewTab>('profile');

  const { donor: donor1, enriching: enriching1 } = useEnrichedDonor(donor1Raw);
  const { donor: donor2, enriching: enriching2 } = useEnrichedDonor(donor2Raw);

  const metrics1 = getDonorMetrics(donor1);
  const metrics2 = getDonorMetrics(donor2);
  const comparison1 = buildComparison(metrics1, metrics2);
  const comparison2 = buildComparison(metrics2, metrics1);

  const swapDonors = () => {
    setDonor1Raw(donor2Raw);
    setDonor2Raw(donor1Raw);
  };

  return (
    <div className="bg-white rounded-[2rem] p-8 shadow-sm border border-slate-100">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-outfit font-extrabold text-xl text-slate-900 leading-none mb-1.5">Donor Comparison</h2>
          <p className="text-slate-500 text-sm font-medium">Search and evaluate two prospective donors side-by-side.</p>
        </div>

        {(donor1 || donor2) && (
          <button
            onClick={swapDonors}
            disabled={!donor1 || !donor2}
            className="shrink-0 inline-flex items-center gap-2 bg-slate-50 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed text-slate-600 font-bold text-xs px-4 py-2.5 rounded-xl transition-colors"
          >
            <ArrowsLeftRight size={16} weight="bold" />
            Swap
          </button>
        )}
      </div>

      {/* Shared view tabs — switching applies to both cards so charts/tables line up for direct comparison */}
      <div className="flex items-center gap-2 mb-6 bg-slate-50 rounded-2xl p-1.5 w-max max-w-full overflow-x-auto">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setView(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-colors whitespace-nowrap ${
              view === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            <Icon size={15} weight={view === key ? 'fill' : 'regular'} />
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
        {/* Column 1 */}
        <div className="flex flex-col gap-4">
          <DonorSelector onSelect={setDonor1Raw} placeholder="Search Donor 1 — Name, Company, Roll No..." />
          <DonorCard
            donor={donor1}
            enriching={enriching1}
            view={view}
            comparison={comparison1}
            onClear={() => setDonor1Raw(null)}
            accentColor="#f97316"
          />
        </div>

        {/* Column 2 */}
        <div className="flex flex-col gap-4">
          <DonorSelector onSelect={setDonor2Raw} placeholder="Search Donor 2 — Name, Company, Roll No..." />
          <DonorCard
            donor={donor2}
            enriching={enriching2}
            view={view}
            comparison={comparison2}
            onClear={() => setDonor2Raw(null)}
            accentColor="#f97316"
          />
        </div>
      </div>
    </div>
  );
};
